import React, { useState, useEffect, useCallback } from 'react';
import {
  Instagram, Facebook, Linkedin, Youtube,
  Settings, Link as LinkIcon, RefreshCw,
  CheckCircle2, AlertCircle,
  ChevronRight, ShieldCheck, Trash2
} from 'lucide-react';
import { Card, CardContent } from '@/ui/card';
import { Button } from '@/ui/button';
import { Badge } from '@/ui/badge';
import { Switch } from '@/ui/switch';
import API from '@/api/api01';
import { toast } from 'sonner';
import { exportToCSV } from '@/utils/exportUtils';
import { Download } from 'lucide-react';
import ConnectIntegrationModal from './ConnectIntegrationModal';
import ManageIntegrationModal from './ManageIntegrationModal';
import CustomApiModal from './CustomApiModal';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from "@/ui/alert-dialog";

const PLATFORMS = [
  {
    id: 'Instagram',
    name: 'Instagram Ads',
    icon: <Instagram className="text-[#E1306C]" />,
    description: 'Sync leads from Instagram Stories, Reels, and Feed Ads.',
    connected: false,
    account: null,
    lastSync: '15m ago'
  },
  {
    id: 'Facebook',
    name: 'Facebook Lead Ads',
    icon: <Facebook className="text-[#1877F2]" />,
    description: 'Direct capture from Facebook Instant Forms and Messenger.',
    connected: false,
    account: null,
    lastSync: '2h ago'
  },
  {
    id: 'LinkedIn',
    name: 'LinkedIn Ads',
    icon: <Linkedin className="text-[#0A66C2]" />,
    description: 'High-quality B2B leads from LinkedIn Campaign Manager.',
    connected: false,
    account: null,
    lastSync: '6h ago'
  },
  {
    id: 'YouTube',
    name: 'YouTube / Google',
    icon: <Youtube className="text-[#FF0000]" />,
    description: 'Lead form extensions on YouTube video and Search ads.',
    connected: false,
    account: null,
    lastSync: '1d ago'
  },
];

export default function SocialCampaigns() {
  const [loading, setLoading] = useState(false);
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [isCustomApiModalOpen, setIsCustomApiModalOpen] = useState(false);
  const [customApis, setCustomApis] = useState<any[]>([]);

  // Management & Disconnect States
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<any>(null);
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const [integrationToDisconnect, setIntegrationToDisconnect] = useState<any>(null);

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    console.log("[SocialCampaigns] Starting fetchData...");
    try {
      setDataLoading(true);
      const [intRes, campRes, customApiRes] = await Promise.all([
        API.get('/superadmin/social-integrations/', { signal }),
        API.get('/superadmin/social-campaigns/', { signal }),
        API.get('/superadmin/custom-api/', { signal }).catch(() => ({ data: [] }))
      ]);
      console.log("[SocialCampaigns] fetchData success:", { 
        integrationsCount: intRes.data.length, 
        campaignsCount: campRes.data.length 
      });
      setIntegrations(intRes.data);
      setCampaigns(campRes.data);
      setCustomApis(customApiRes.data || []);
      if (intRes.data.length > 0 || campRes.data.length > 0) {
        toast.success("Marketing data synchronized");
      } else {
        console.log("[SocialCampaigns] No records found.");
      }
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') {
        console.log("[SocialCampaigns] fetchData aborted");
        return;
      }
      console.error("[SocialCampaigns] fetchData error:", err);
      toast.error("Failed to load connections");
    } finally {
      setDataLoading(false);
      console.log("[SocialCampaigns] fetchData finished.");
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, []);

  const handleSync = async () => {
    try {
      setLoading(true);
      const res = await API.post('/superadmin/social-leads/sync');
      toast.success(res.data.message || "Synchronized successfully!");
      fetchData();
    } catch (err) {
      console.error("[SocialCampaigns] Sync error:", err);
      toast.error("Failed to synchronize leads");
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectAction = async () => {
    if (!integrationToDisconnect) return;
    try {
      setLoading(true);
      await API.delete(`/superadmin/social-integrations/${integrationToDisconnect.id}`);
      toast.success("Channel disconnected");
      setIsDisconnectDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to disconnect");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSync = async (integrationId: string, currentStatus: boolean) => {
    try {
      // Local optimistic update
      setIntegrations(prev => prev.map(int => 
        int.id === integrationId ? { ...int, status: currentStatus ? "Paused" : "Connected" } : int
      ));
      
      await API.put(`/superadmin/social-integrations/${integrationId}/toggle`, {
        is_active: !currentStatus
      });
      toast.success(`Active sync ${!currentStatus ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      // Revert optimistic update
      setIntegrations(prev => prev.map(int => 
        int.id === integrationId ? { ...int, status: currentStatus ? "Connected" : "Paused" } : int
      ));
      toast.error("Failed to toggle sync status");
    }
  };

  const openManageModal = (integration: any) => {
    // Delay slightly to allow DropdownMenu to close and prevent focus/interaction locks
    setTimeout(() => {
      console.log("[SocialCampaigns] Opening Manage Modal for:", integration.platform);
      setSelectedIntegration(integration);
      setIsManageModalOpen(true);
    }, 100);
  };

  const openDisconnectDialog = (integration: any) => {
    setTimeout(() => {
      console.log("[SocialCampaigns] Opening Disconnect Dialog for:", integration.platform);
      setIntegrationToDisconnect(integration);
      setIsDisconnectDialogOpen(true);
    }, 100);
  };

  return (
    <div className="space-y-8 pb-10">
      {/* Integrations Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-1">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
            <LinkIcon size={24} className="text-purple-600" /> Platform Connections
          </h2>
          <p className="text-[13px] text-slate-500 font-medium mt-1">Manage APIs and lead synchronization for your marketing channels.</p>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => {
              if (campaigns.length === 0) {
                  toast.error("No campaigns to export");
                  return;
              }
              const exportData = campaigns.map(c => ({
                  ID: c.id,
                  Name: c.name,
                  Platform: c.platform,
                  Status: c.status,
                  LeadsCount: c.leads_count,
                  TargetAudience: c.target_audience || 'N/A'
              }));
              exportToCSV(exportData, `marketing_campaigns_${new Date().toISOString().split('T')[0]}.csv`);
              toast.success("Campaign data exported");
          }} className="rounded-xl border-slate-200 h-10 px-5 text-[11px] font-bold uppercase tracking-widest transition-all hover:bg-slate-50 dark:hover:bg-white/5 active:scale-95 shadow-sm">
            <Download size={14} className="mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={handleSync} disabled={loading} className="rounded-xl border-slate-200 h-10 px-5 text-[11px] font-bold uppercase tracking-widest transition-all hover:bg-slate-50 dark:hover:bg-white/5 active:scale-95 shadow-sm">
            <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Syncing...' : 'Sync All'}
          </Button>
          <Button
            size="sm"
            onClick={() => setIsConnectModalOpen(true)}
            className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white h-10 px-6 text-[11px] font-bold uppercase tracking-widest shadow-lg shadow-purple-500/20 transition-all active:scale-95"
          >
            <LinkIcon size={14} className="mr-2" />
            Add Channel
          </Button>
          <Button
            size="sm"
            onClick={() => setIsCustomApiModalOpen(true)}
            className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white h-10 px-6 text-[11px] font-bold uppercase tracking-widest shadow-lg shadow-indigo-500/20 transition-all active:scale-95"
          >
            <LinkIcon size={14} className="mr-2" />
            Custom API
          </Button>

        </div>
      </div>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PLATFORMS.map((platform) => {
          const connectedPlatform = integrations.find(i => i.platform.toLowerCase() === platform.id.toLowerCase());
          const isConnected = !!connectedPlatform;
          const accountName = connectedPlatform?.account_name || 'Not Linked';

          return (
            <Card key={platform.id} className="border-none shadow-sm rounded-3xl overflow-hidden group hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 bg-card border border-transparent hover:border-purple-100">
              <CardContent className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="p-4 rounded-2xl bg-muted shadow-sm border border-slate-100 dark:border-slate-800 group-hover:scale-110 transition-transform">
                    {React.cloneElement(platform.icon as React.ReactElement, { size: 32 })}
                  </div>
                  <Badge variant={isConnected ? "secondary" : "outline"} className={`rounded-full px-3 py-1 border-none font-black text-[10px] uppercase tracking-widest ${isConnected ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-400'}`}>
                    {isConnected ? (
                      <span className="flex items-center gap-1.5"><CheckCircle2 size={12} /> Live</span>
                    ) : (
                      <span className="flex items-center gap-1.5"><AlertCircle size={12} /> Setup</span>
                    )}
                  </Badge>
                </div>

                <h3 className="text-[15px] font-bold text-slate-900 dark:text-white mb-2 leading-tight tracking-tight">{platform.name}</h3>
                <p className="text-[13px] text-slate-500 dark:text-slate-400 line-clamp-2 mb-6 leading-relaxed">
                  {platform.description}
                </p>


                {isConnected ? (
                  <div className="space-y-3 bg-muted p-5 rounded-[1.5rem] border border-slate-100 dark:border-slate-800">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400 font-black uppercase tracking-widest text-[9px]">Linked Account</span>
                      <span className="font-bold text-slate-700 dark:text-slate-200">{accountName}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400 font-black uppercase tracking-widest text-[9px]">Last Sync</span>
                      <span className="font-bold text-slate-500">{platform.lastSync}</span>
                    </div>
                  </div>
                ) : (
                  <Button onClick={() => setIsConnectModalOpen(true)} className="w-full h-12 rounded-2xl bg-slate-900 hover:bg-black dark:bg-slate-800 dark:hover:bg-slate-700 font-black tracking-tight text-sm">
                    Initialize Connection
                  </Button>
                )}

                <div className="mt-6 flex items-center justify-between">
                  {isConnected ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-purple-600 h-9 rounded-xl px-3 hover:bg-purple-50 transition-colors">
                          Manage Account <ChevronRight size={12} className="ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="rounded-2xl shadow-2xl border-none bg-white dark:bg-slate-900 p-2 min-w-[200px]">
                        <DropdownMenuItem
                          onClick={() => openManageModal(connectedPlatform)}
                          className="gap-3 p-3 rounded-xl cursor-pointer focus:bg-slate-50 dark:focus:bg-slate-800 font-bold text-sm text-slate-600 dark:text-slate-300"
                        >
                          <div className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            <Settings size={16} className="text-slate-500" />
                          </div>
                          Platform Settings
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-3 p-3 rounded-xl focus:bg-red-50 dark:focus:bg-red-900/10 focus:text-red-600 cursor-pointer text-red-500 font-bold text-sm"
                          onClick={() => openDisconnectDialog(connectedPlatform)}
                        >
                          <div className="h-8 w-8 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                            <Trash2 size={16} className="text-red-500" />
                          </div>
                          Disconnect Channel
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <span className="text-[10px] text-slate-300 font-black uppercase tracking-widest italic ml-1">Not Connected</span>
                  )}

                  {isConnected && (
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Active Sync</span>
                      <Switch 
                        checked={connectedPlatform.status === "Connected"} 
                        onCheckedChange={() => handleToggleSync(connectedPlatform.id, connectedPlatform.status === "Connected")}
                        className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-200 dark:data-[state=unchecked]:bg-slate-700" 
                      />
                    </div>
                  )}
                </div>

              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Custom APIs Grid */}
      {customApis.length > 0 && (
        <div className="mt-8 mb-6">
          <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight mb-4 ml-1">Custom API Sources</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {customApis.map((api) => (
              <Card key={api.id} className="border-none shadow-sm rounded-3xl overflow-hidden group hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 bg-card border border-transparent hover:border-indigo-100">
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className="p-4 rounded-2xl bg-muted shadow-sm border border-slate-100 dark:border-slate-800 text-indigo-500">
                      <LinkIcon size={32} />
                    </div>
                    <Badge variant="secondary" className="bg-emerald-50 text-emerald-600 rounded-full px-3 py-1 border-none font-black text-[10px] uppercase tracking-widest shadow-sm">
                      <span className="flex items-center gap-1.5"><CheckCircle2 size={12} /> Live</span>
                    </Badge>
                  </div>
                  <h3 className="font-black text-xl mb-2 tracking-tight text-slate-900 dark:text-white">{api.name}</h3>
                  <p className="text-sm text-slate-400 dark:text-slate-500 truncate mb-6 leading-relaxed font-medium">
                    {api.url}
                  </p>
                  <Button 
                    variant="ghost" 
                    onClick={async () => {
                      try {
                        await API.delete(`/superadmin/custom-api/${api.id}`);
                        toast.success("Custom API removed");
                        fetchData();
                      } catch (err) {
                        toast.error("Failed to remove Custom API");
                      }
                    }}
                    className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 h-10 rounded-xl text-xs font-bold"
                  >
                    <Trash2 size={14} className="mr-2" /> Remove
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Campaigns Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <h3 className="text-xl font-black text-foreground tracking-tight mb-4 ml-1">Live Syncing Campaigns</h3>
          <div className="bg-card rounded-[2rem] shadow-sm overflow-hidden border border-slate-50 dark:border-slate-800">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 border-b border-white/5">
                  <tr>
                    <th className="px-8 py-4 text-left text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70">Campaign Strategy</th>
                    <th className="px-8 py-4 text-left text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70">Source</th>
                    <th className="px-8 py-4 text-left text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70">Internal Status</th>
                    <th className="px-8 py-4 text-right text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 pr-8">Leads</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                  {campaigns.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="p-20 text-center">
                        <div className="flex flex-col items-center justify-center text-slate-400">
                          <LinkIcon size={40} className="mb-4 opacity-10" />
                          <p className="text-lg font-bold">No active campaigns detected</p>
                          <p className="text-sm font-medium">Connect a channel to start syncing data</p>
                        </div>
                      </td>
                    </tr>
                  ) : campaigns.map((camp) => (
                    <tr key={camp.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-white/5">
                      <td className="px-8 py-5">
                        <div className="text-[13px] font-bold text-slate-900 dark:text-white">{camp.name}</div>
                        <div className="text-[10px] text-muted-foreground/60 font-bold uppercase tracking-widest mt-0.5">{camp.target_audience || 'Broad Audience'}</div>
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center border border-white/5 shadow-sm">
                            {camp.platform === 'Instagram' && <Instagram size={14} className="text-[#E1306C]" />}
                            {camp.platform === 'Facebook' && <Facebook size={14} className="text-[#1877F2]" />}
                            {camp.platform === 'LinkedIn' && <Linkedin size={14} className="text-[#0A66C2]" />}
                            {camp.platform === 'YouTube' && <Youtube size={14} className="text-[#FF0000]" />}
                          </div>
                          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">{camp.platform}</span>
                        </div>
                      </td>
                      <td className="px-8 py-5 text-[13px] font-semibold text-slate-900 dark:text-white">
                        <Badge className="bg-emerald-500/10 text-emerald-600 border-none rounded-full px-3 py-0.5 font-bold text-[10px] uppercase tracking-widest">
                          {camp.status}
                        </Badge>
                      </td>
                      <td className="px-8 py-5 text-right font-bold text-slate-900 dark:text-white text-[13px] pr-8">{camp.leads_count}</td>
                    </tr>

                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight mb-4 ml-1">Security Standards</h3>
          <Card className="rounded-[2rem] border-none shadow-sm bg-indigo-600 text-white p-8 relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent)] transition-all duration-500 group-hover:scale-125" />
            <div className="relative z-10">
              <div className="h-16 w-16 rounded-[1.25rem] bg-white/20 backdrop-blur-md flex items-center justify-center mb-6 border border-white/20">
                <ShieldCheck size={36} />
              </div>
              <h4 className="text-2xl font-black mb-3">Enterprise API Integration</h4>
              <p className="text-sm text-indigo-100 font-medium leading-relaxed mb-6">
                We use official OAuth 2.0 protocols. Your access tokens are encrypted and never stored in plain text. Leads are pushed via 256-bit secure webhooks.
              </p>
              <Button 
                variant="ghost" 
                onClick={() => toast.info("Security Portal Coming Soon", { description: "We are finalizing the enterprise security dashboard." })}
                className="w-full rounded-2xl bg-white/10 hover:bg-white hover:text-indigo-600 border border-white/20 font-black h-12 uppercase tracking-widest text-xs transition-all"
              >
                Security Portal
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Modals & Dialogs */}
      <ConnectIntegrationModal
        open={isConnectModalOpen}
        onOpenChange={setIsConnectModalOpen}
        onSuccess={fetchData}
      />

      <CustomApiModal 
        open={isCustomApiModalOpen}
        onOpenChange={setIsCustomApiModalOpen}
        onSuccess={fetchData}
      />

      <ManageIntegrationModal
        open={isManageModalOpen}
        onOpenChange={setIsManageModalOpen}
        integration={selectedIntegration}
        onSuccess={fetchData}
      />

      <AlertDialog open={isDisconnectDialogOpen} onOpenChange={setIsDisconnectDialogOpen}>
        <AlertDialogContent className="rounded-[3rem] border-none shadow-2xl p-10 max-w-[480px]">
          <AlertDialogHeader>
            <div className="h-20 w-20 rounded-[2rem] bg-red-50 dark:bg-red-900/10 flex items-center justify-center text-red-500 mb-6 group">
              <Trash2 size={40} className="group-hover:rotate-12 transition-transform" />
            </div>
            <AlertDialogTitle className="text-3xl font-black tracking-tight text-slate-900 dark:text-white leading-tight">Disconnect Channel?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400 font-bold text-base leading-relaxed mt-2">
              All automatic lead synchronization for <span className="text-slate-900 dark:text-white font-black underline decoration-red-500/30 underline-offset-4">{integrationToDisconnect?.account_name}</span> will stop immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 flex flex-col sm:flex-row gap-4">
            <AlertDialogCancel className="rounded-2xl h-14 flex-1 font-black text-slate-400 border-slate-100 hover:bg-slate-50 transition-all uppercase tracking-widest text-xs">
              Stay Connected
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnectAction}
              className="rounded-2xl h-14 flex-1 bg-red-500 hover:bg-red-600 font-black shadow-2xl shadow-red-200 uppercase tracking-widest text-xs transition-all border-none"
            >
              Yes, Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}